import { z } from 'zod';

import { SeveritySchema } from '../enums.js';

import { IsoDateSchema, UuidSchema } from './common.js';
import { FreshnessResponseSchema } from './sources.js';

// alert-service GET /dashboard and GET /search (prototype dashboard and command palette).
// Everything here is already filtered by the caller's visibility (BR-021).

export const DashboardSchema = z.object({
  kpis: z.object({
    open: z.number().int().nonnegative(),
    needDecision: z.number().int().nonnegative(),
    waiting: z.number().int().nonnegative(),
    closed: z.number().int().nonnegative(),
  }),
  /** Soonest PO first, then severity (prototype "Fix first"). */
  fixFirst: z.array(
    z.object({
      alertId: UuidSchema,
      code: z.string(),
      partPn: z.string(),
      description: z.string(),
      assemblyName: z.string(),
      severity: SeveritySchema,
      pdmQty: z.number().nullable(),
      erpQty: z.number().nullable(),
      poCode: z.string().nullable(),
      poDays: z.number().int().nullable(),
    }),
  ),
  severityCounts: z.object({
    0: z.number().int().nonnegative(),
    1: z.number().int().nonnegative(),
    2: z.number().int().nonnegative(),
  }),
  byAssembly: z.array(
    z.object({
      assemblyId: UuidSchema,
      name: z.string(),
      open: z.number().int().nonnegative(),
      bySeverity: z.tuple([z.number().int(), z.number().int(), z.number().int()]),
    }),
  ),
  /** "Where alerts are right now": New, Draft ready, Waiting for reply, Verifying. */
  stages: z.array(
    z.object({ statusKey: z.string(), label: z.string(), count: z.number().int().nonnegative() }),
  ),
  /** POs releasing in the next 14 days. */
  posSoon: z.array(
    z.object({
      alertId: UuidSchema,
      code: z.string(),
      poCode: z.string(),
      partPn: z.string(),
      releaseDate: IsoDateSchema,
      days: z.number().int(),
    }),
  ),
  freshness: FreshnessResponseSchema,
});
export type Dashboard = z.infer<typeof DashboardSchema>;

export const SearchQuerySchema = z.object({ q: z.string().trim().min(1).max(200) });

export const SearchResponseSchema = z.object({
  items: z.array(
    z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('page'), label: z.string(), page: z.string() }),
      z.object({ kind: z.literal('alert'), label: z.string(), alertId: UuidSchema }),
    ]),
  ),
});
export type SearchResponse = z.infer<typeof SearchResponseSchema>;

/** Server-sent events on `GET /stream`. */
export const StreamMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('alert.updated'), alertId: UuidSchema, version: z.number().int() }),
  z.object({ type: z.literal('toast'), text: z.string() }),
]);
export type StreamMessage = z.infer<typeof StreamMessageSchema>;
